import React, { FunctionComponent } from 'react';
import classNames from 'classnames';
import { EventContentsSectionProps, EventContentsSectionState, RegisterModalProps, LoginModalProps, ModalMode } from './types';
import { EventContent } from '../../../types/eventDetail';
import { Component as RegisterModal } from '../registerModal/component';
import { Component as LoginModal } from '../loginModal/component';
import { Presentation as EventEntryModal } from '../../molecules/eventEntryModal/presentation';

type Props = EventContentsSectionProps & {
  isPc: boolean;
  state: EventContentsSectionState;
  onApplyClick: (eventContentId: number) => void;
  onApplyConfirm: () => void;
  onCancelClick: (eventContentId: number) => void;
  onCancelConfirm: () => void;
  onModalClose: () => void;
  registerModalProps: RegisterModalProps;
  isEntryModalOpen: boolean;
  isLoginModalOpen: boolean;
  redirectUrl: string;
  isRecruitmentOrEventOrTvOrCompanyPath: () => boolean;
  loginModalProps: LoginModalProps;
  modalMode: ModalMode;
};

export const Presentation: FunctionComponent<Props> = ({
  eventTitle,
  isPc,
  eventContents,
  currentUser,
  state,
  onApplyClick,
  onApplyConfirm,
  onCancelClick,
  onCancelConfirm,
  onModalClose,
  isEntryModalOpen,
  modalMode,
  registerModalProps,
  loginModalProps
}) => {

  const renderApplyButton = (ec: EventContent, buttonClass: string) => {
    if (ec.isEnded) {
      return <span className={isPc ? 'event-contents-table-full' : 'event-contents-table-sp-full'}>受付終了</span>;
    }
    if (ec.isApplied) {
      return (
        <>
          <input type="button" className={isPc ? 'event-contents-table-apply-btn-done' : 'event-contents-table-sp-full-done'} value="申し込み済" disabled />
          <input
            type="button"
            className={classNames('cancel-btn', 'gtm-cancel-view-count', {
              'event-contents-table-apply-btn-cancel': isPc,
              'event-contents-table-sp-full-cancel': !isPc,
            })}
            value="キャンセル"
            data-event="click"
            data-category="eventcontent"
            data-value="cancel_event"
            onClick={() => onCancelClick(ec.id)}
            disabled={state.isProcessing}
            />
        </>
      );
    }
    // 申し込み済みの場合はキャンセルできるよう、isAppliedの判定より後に置く
    if (ec.isApplicationClosed) {
      return <span className={isPc ? 'event-contents-table-full' : 'event-contents-table-sp-full'}>受付終了</span>;
    }
    if (ec.isCancelled) {
      return (
        <>
          <input
            type="button"
            className={buttonClass}
            value="再申し込み"
            data-event="click"
            data-category="eventcontent"
            data-value="re_entry_event"
            onClick={() => onApplyClick(ec.id)}
            disabled={state.isProcessing}
            />
          <input
            type="button"
            className={classNames('cancel-btn', 'gtm-cancel-view-count', {
              'event-contents-table-apply-btn-cancel': isPc,
              'event-contents-table-sp-full-cancel': !isPc,
            })}
            value="キャンセル済"
            disabled
          />
        </>
      );
    }
    if (ec.isFull) {
      return <span className={isPc ? 'event-contents-table-full' : 'event-contents-table-sp-full'}>満員</span>;
    }
    if (!currentUser) {
      return (
        <input
          type="button"
          className={buttonClass}
          value="申し込む"
          data-event="click"
          data-category="eventcontent"
          data-value="entry_event"
          onClick={() => onApplyClick(ec.id)}
          disabled={state.isProcessing}
        />
      );
    }
    return (
      <input
        type="button"
        className={buttonClass}
        value="申し込む"
        data-event="click"
        data-category="eventcontent"
        data-value="entry_event"
        onClick={() => onApplyClick(ec.id)}
        disabled={state.isProcessing}
      />
    );
  };

  return (
    <div>
      {isPc ? (
        <table className="event-contents-table gtm-entry-view-count" data-event-name="entry on screen" data-category="eventcontent" data-value="entry_event">
          <thead>
            <tr className="event-contents-table-heading">
              <th className="event-contents-table-heading-date">開催日</th>
              <th className="event-contents-table-heading-time">時間</th>
              <th className="event-contents-table-heading-place">場所（会場）</th>
              <th className="event-contents-table-heading-apply">申し込み</th>
            </tr>
          </thead>
          <tbody>
            {eventContents.map(ec => (
              <tr key={ec.id} className="js-event_contents">
                <td className="event-contents-table-date">
                  {ec.startDate}
                </td>
                <td className="event-contents-table-time">
                  {ec.startTime} - {ec.endTime}
                </td>
                <td className="event-contents-table-place">{ec.place}</td>
                <td className="event-contents-table-apply">
                  {renderApplyButton(ec, 'event-contents-table-apply-btn')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="event-contents-table-sp gtm-entry-view-count" data-category="eventcontent" data-value="entry_event">
          {eventContents.map(ec => (
            <div key={ec.id} className="event-contents-table-sp-container">
              <div className="event-contents-table-sp-date">
                {ec.startDate}
              </div>
              <div className="event-contents-table-sp-time">
                <div className="event-contents-table-sp-time-title">時間</div>
                <div className="event-contents-table-sp-time-detail">
                  {ec.startTime} - {ec.endTime}
                </div>
              </div>
              <div className="event-contents-table-sp-place">
                <div className="event-contents-table-sp-place-title">場所（会場）</div>
                <div className="event-contents-table-sp-place-detail">{ec.place}</div>
              </div>
              <div className="event-contents-table-sp-apply">
                {renderApplyButton(ec, 'event-contents-table-sp-apply-btn')}
              </div>
            </div>
          ))}
        </div>
      )}

      {isEntryModalOpen && state.selectedEventContent && (
        <EventEntryModal
          isOpen={isEntryModalOpen}
          mode={modalMode}
          eventTitle={eventTitle}
          startDate={state.selectedEventContent.startDate}
          startTime={state.selectedEventContent.startTime}
          endTime={state.selectedEventContent.endTime}
          place={state.selectedEventContent.place}
          isProcessing={state.isProcessing}
          onConfirm={onApplyConfirm}
          onCancel={onCancelConfirm}
          onClose={onModalClose}
        />
      )}

      <RegisterModal
        {...registerModalProps}
      />

      <LoginModal
        {...loginModalProps}
      />
    </div>
  );
};
